import axios from 'axios';
import { getAuthHeader } from './authService';
import { getApiUrl } from '../utils/config';

/**
 * Interface đại diện cho thông tin xếp hạng người dùng
 */
export interface UserRanking {
  userId: number;
  username: string;
  fullName: string;
  profileImageUrl: string;
  maintenanceRatio: number;
  totalDistance: number;
  rank: number;
  validDays: number;
  totalDays: number;
}

/**
 * Interface đại diện cho thông tin xếp hạng đội
 */
export interface TeamRanking {
  userId: number;  // đây là teamId
  username: string;  // đây là team name
  fullName: string;
  profileImageUrl: string;
  maintenanceRatio: number;
  totalDistance: number;
  rank: number;
  validDays: number;
  totalDays: number;
}

/**
 * Lấy xếp hạng của tất cả người dùng trong giải đấu
 * @param tournamentId ID của giải đấu
 * @param startDate Ngày bắt đầu (tùy chọn)
 * @param endDate Ngày kết thúc (tùy chọn)
 * @returns Danh sách xếp hạng người dùng
 */
export const getTournamentUserRankings = async (
  tournamentId: number, 
  startDate?: string, 
  endDate?: string
): Promise<UserRanking[]> => {
  const authHeader = getAuthHeader();
  const params = startDate && endDate ? { startDate, endDate } : {};
  
  const res = await axios.get(
    getApiUrl(`/api/rankings/tournament/${tournamentId}/users`), 
    {
      headers: { 'Authorization': authHeader },
      params
    }
  );
  return res.data.data;
};

/**
 * Lấy xếp hạng của tất cả đội trong giải đấu
 * @param tournamentId ID của giải đấu
 * @param startDate Ngày bắt đầu (tùy chọn)
 * @param endDate Ngày kết thúc (tùy chọn)
 * @returns Danh sách xếp hạng đội
 */
export const getTournamentTeamRankings = async (
  tournamentId: number,
  startDate?: string, 
  endDate?: string
): Promise<TeamRanking[]> => {
  const authHeader = getAuthHeader();
  const params = startDate && endDate ? { startDate, endDate } : {};
  
  const res = await axios.get(
    getApiUrl(`/api/rankings/tournament/${tournamentId}/teams`), 
    {
      headers: { 'Authorization': authHeader },
      params
    }
  );
  return res.data.data;
};

/**
 * Lấy tỷ lệ duy trì (%) của người dùng trong giải đấu
 * @param tournamentId ID của giải đấu
 * @param userId ID của người dùng
 * @param startDate Ngày bắt đầu (tùy chọn)
 * @param endDate Ngày kết thúc (tùy chọn)
 * @returns Tỷ lệ duy trì dạng phần trăm
 */
export const getUserMaintenanceRatio = async (
  tournamentId: number, 
  userId: number,
  startDate?: string, 
  endDate?: string
): Promise<number> => {
  const authHeader = getAuthHeader();
  const params = startDate && endDate ? { startDate, endDate } : {};
  
  const res = await axios.get(
    getApiUrl(`/api/rankings/tournament/${tournamentId}/user/${userId}/maintenance-ratio`), 
    {
      headers: { 'Authorization': authHeader },
      params
    }
  );
  return res.data.data;
};

/**
 * Lấy chi tiết thông tin tỷ lệ duy trì của người dùng trong giải đấu
 * @param tournamentId ID của giải đấu
 * @param userId ID của người dùng
 * @param startDate Ngày bắt đầu (tùy chọn)
 * @param endDate Ngày kết thúc (tùy chọn)
 * @returns Thông tin chi tiết về tỷ lệ duy trì và xếp hạng
 */
export const getUserMaintenanceDetails = async (
  tournamentId: number, 
  userId: number,
  startDate?: string, 
  endDate?: string
): Promise<UserRanking> => {
  const authHeader = getAuthHeader();
  const params = startDate && endDate ? { startDate, endDate } : {};
  
  const res = await axios.get(
    getApiUrl(`/api/rankings/tournament/${tournamentId}/user/${userId}/details`), 
    {
      headers: { 'Authorization': authHeader },
      params
    }
  );
  return res.data.data;
}; 